import axios from 'axios';
import { stateCodeFromGstin, stateNameByCode } from './indiaStates';

// Talks straight to the live GST lookup API. Its CORS allow-list already
// includes localhost:3000, so no proxy is needed in dev.
//
// Deliberately a plain axios instance, NOT the app's `apiClient`: that one
// attaches our auth token and points at our own backend — neither of which
// should go to a third-party service.
const GST_API = process.env.REACT_APP_GST_API_URL || 'https://gstapi.conceptra.co.in';

const gstClient = axios.create({
  baseURL: GST_API,
  // The API drives a headless browser against services.gst.gov.in, so it's slow.
  timeout: 90000,
  headers: { 'Content-Type': 'application/json' },
});

// Step 1 — opens a real portal session server-side (lives ~5 min).
// Returns { sessionId, captcha } where captcha is a data:image/png;base64 string.
export const fetchCaptcha = async (gstin) => {
  const { data } = await gstClient.post('/gst/captcha', { gstin: gstin.trim().toUpperCase() });
  return data;
};

// Step 2 — sessionId is SINGLE-USE: it's consumed whether or not the captcha
// was right, so any retry needs a fresh captcha.
export const verifyGstin = async (sessionId, captcha) => {
  const { data } = await gstClient.post('/gst/verify', { sessionId, captcha: captcha.trim() });
  return data;
};

// --- Mapping: GST portal taxpayer record -> our Client.gstins[] shape ---
// The portal's own abbreviations (lgnm, sts, dty, ctj/stj, pradr…) are
// translated here so they never leak into the schema or the UI.

const REGISTRATION_TYPE_RULES = [
  [/composition/i, 'Composition'],
  [/casual/i, 'Casual'],
  [/non[\s-]?resident/i, 'NRTP'],
  [/sez\s*developer/i, 'SEZ_Developer'],
  [/sez/i, 'SEZ_Unit'],
  [/input service distributor|\bisd\b/i, 'ISD'],
  [/deductor|\btds\b/i, 'TDS'],
  [/collector|e-?commerce|\btcs\b/i, 'TCS'],
];

const mapRegistrationType = (dty) => {
  const rule = REGISTRATION_TYPE_RULES.find(([re]) => re.test(dty || ''));
  return rule ? rule[1] : 'Regular';
};

// Anything unrecognised stays Active so we never produce a value our enum rejects.
const mapStatus = (sts) => {
  const s = (sts || '').trim().toLowerCase();
  if (s === 'cancelled') return 'Cancelled';
  if (s === 'suspended') return 'Suspended';
  if (s === 'inactive') return 'Cancelled';
  return 'Active';
};

// Portal dates are DD/MM/YYYY; our date inputs want YYYY-MM-DD.
const toISODate = (value) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((value || '').trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
};

// In practice the portal often returns only the flattened `adr` string and no
// structured `addr` object, e.g.
//   "7th Floor, C-071A, Super mart 1, DLF Phase iv, Gurgaon, Gurugram, Haryana, 122002"
// It's only reliably parseable from the END: the last comma-part is the
// pincode, then the state, then the city. Whatever remains is the street
// address, which we split evenly across the two address lines.
const parseFlatAddress = (flat) => {
  const parts = (flat || '').split(',').map((s) => s.trim()).filter(Boolean);
  const out = { line1: '', line2: '', city: '', state: '', pincode: '' };
  if (parts.length === 0) return out;

  if (/^\d{6}$/.test(parts[parts.length - 1])) out.pincode = parts.pop();
  if (parts.length) out.state = parts.pop();
  if (parts.length) out.city = parts.pop();

  const half = Math.ceil(parts.length / 2);
  out.line1 = parts.slice(0, half).join(', ');
  out.line2 = parts.slice(half).join(', ');

  // Pincode wasn't the final part after all — fall back to finding it anywhere.
  if (!out.pincode) out.pincode = (flat.match(/\b(\d{6})\b/) || [])[1] || '';

  return out;
};

// pradr may carry a structured `addr` object and/or the flattened `adr` string.
// Prefer structured fields where present, and fill any gap from the parsed
// flat string, so we get city/state either way.
const mapAddress = (pradr, gstin) => {
  const addr = (pradr && pradr.addr) || {};
  const parsed = parseFlatAddress((pradr && pradr.adr) || '');

  // First two digits of a GSTIN ARE the state code, and a principal place of
  // business is by definition in the registration's own state — so the code
  // comes off the number and the NAME comes off the code, rather than from the
  // portal's free-text `stcd`. That string is only a fallback for the codes we
  // don't recognise, so an unfamiliar territory still shows something.
  const stateCode = stateCodeFromGstin(gstin);
  return {
    line1: [addr.bno, addr.flno, addr.bnm].filter(Boolean).join(', ') || parsed.line1,
    line2: [addr.st, addr.loc].filter(Boolean).join(', ') || parsed.line2,
    city: addr.dst || addr.city || parsed.city,
    state: stateNameByCode(stateCode) || addr.stcd || parsed.state,
    stateCode,
    pincode: addr.pncd || parsed.pincode,
  };
};

// Returns { gstin, suggested }: `gstin` drops straight into the GSTIN form;
// `suggested` holds client-level fields the caller may use to fill blanks.
export function mapTaxpayerToGstin(data) {
  const gstin = data.gstin || '';

  return {
    gstin: {
      gstin,
      stateCode: stateCodeFromGstin(gstin),
      registrationDate: toISODate(data.dtReg),
      registrationType: mapRegistrationType(data.dty),
      // The portal doesn't expose QRMP opt-in on the taxpayer record.
      filingFrequency: 'Monthly',
      status: mapStatus(data.sts),
      cancellationDate: toISODate(data.dtDReg),
      centralJurisdiction: data.ctj || '',
      stateJurisdiction: data.stj || '',
      arn: '',
      provisionalId: '',
      ppob: mapAddress(data.pradr, gstin),
      apob: [],
    },
    suggested: {
      legalName: data.lgnm || '',
      displayName: data.tradeNam || data.lgnm || '',
      // Characters 3-12 of a GSTIN are the holder's PAN.
      pan: gstin.length >= 12 ? gstin.slice(2, 12) : '',
    },
  };
}
